import React from 'react';
import { UnifiedThemeProvider, createUnifiedTheme, palettes } from '@backstage/theme';
import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';

// ── Dark theme — deep dark sidebar ────────────────────────────────────────
// Extends Backstage's dark palette with our custom navigation colours so the
// sidebar is visually distinct and high-contrast in dark mode.
const customDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    navigation: {
      background: '#161616',
      indicator: '#7df3e1',
      color: 'rgba(208, 208, 208, 0.82)',
      selectedColor: '#ffffff',
      navItem: {
        hoverBackground: 'rgba(255, 255, 255, 0.05)',
      },
      submenu: {
        background: '#2a2a2a',
      },
    },
  },
});

// ── Light theme — light sidebar ────────────────────────────────────────────
// Extends Backstage's light palette to give the sidebar a proper light look
// (light grey background, dark text, darker teal indicator) rather than the
// Backstage default dark sidebar that appears even in light mode.
const customLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    navigation: {
      background: '#f5f5f5',
      indicator: '#00897b',          // darker teal — clearly visible on light bg
      color: 'rgba(0, 0, 0, 0.82)',
      selectedColor: '#000000',
      navItem: {
        hoverBackground: 'rgba(0, 0, 0, 0.06)',
      },
      submenu: {
        background: '#e8e8e8',
      },
    },
  },
});

// ── AppTheme registry ──────────────────────────────────────────────────────
// Passed to createApp({ themes }) so Backstage's theme-switcher in Settings
// offers both options, each backed by the correct sidebar palette above.
export const appThemes = [
  {
    id: 'light',
    title: 'Light Theme',
    variant: 'light' as const,
    icon: <LightIcon />,
    Provider: ({ children }: { children: React.ReactNode }) => (
      <UnifiedThemeProvider theme={customLightTheme}>
        {children}
      </UnifiedThemeProvider>
    ),
  },
  {
    id: 'dark',
    title: 'Dark Theme',
    variant: 'dark' as const,
    icon: <DarkIcon />,
    Provider: ({ children }: { children: React.ReactNode }) => (
      <UnifiedThemeProvider theme={customDarkTheme}>
        {children}
      </UnifiedThemeProvider>
    ),
  },
];
